/**
 * Stripe.js, loaded only when this deployment takes card payments through Stripe.
 *
 * ## Card data never touches our servers
 *
 * The card form is Stripe's Payment Element: an iframe served by Stripe, typed
 * into on Stripe's origin and sent to Stripe. This page never sees a card
 * number, and nothing it sends to the platform carries one — the platform
 * receives an order id and an idempotency key, and learns the outcome from
 * Stripe's signed webhook.
 *
 * ## Why a script tag and not a package
 *
 * Stripe requires Stripe.js to be loaded from `js.stripe.com` (it is what keeps
 * the integration PCI SAQ-A); the npm loader does exactly this and nothing
 * more. Writing the twenty lines here keeps the lockfile unchanged and loads
 * nothing at all for a deployment on the fake gateway.
 *
 * The publishable key comes from the API (`GET consumer/payments/config`),
 * never from this bundle, so the account it names is chosen where the secret
 * key is.
 */

export interface StripeError {
  readonly type: string;
  readonly code?: string;
  readonly decline_code?: string;
  readonly message?: string;
}

export interface StripePaymentElement {
  mount(target: HTMLElement): void;
  destroy(): void;
  on(event: 'ready' | 'change', handler: (event: { complete?: boolean }) => void): void;
}

export interface StripeElements {
  create(type: 'payment', options?: Record<string, unknown>): StripePaymentElement;
  submit(): Promise<{ readonly error?: StripeError }>;
  update(options: Record<string, unknown>): void;
}

export interface StripeJs {
  elements(options: Record<string, unknown>): StripeElements;
  confirmPayment(options: {
    readonly elements?: StripeElements;
    readonly clientSecret: string;
    readonly redirect: 'if_required';
    readonly confirmParams: { readonly return_url: string };
  }): Promise<{ readonly error?: StripeError; readonly paymentIntent?: { readonly status: string } }>;
  retrievePaymentIntent(
    clientSecret: string,
  ): Promise<{ readonly paymentIntent?: { readonly status: string }; readonly error?: StripeError }>;
}

type StripeFactory = (publishableKey: string) => StripeJs;

declare global {
  interface Window {
    Stripe?: StripeFactory;
  }
}

const STRIPE_JS = 'https://js.stripe.com/v3/';
let loading: Promise<StripeFactory> | undefined;

function loadScript(): Promise<StripeFactory> {
  if (window.Stripe !== undefined) return Promise.resolve(window.Stripe);
  loading ??= new Promise<StripeFactory>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = STRIPE_JS;
    script.async = true;
    script.onload = () => {
      if (window.Stripe !== undefined) resolve(window.Stripe);
      else reject(new Error('Stripe.js loaded without Stripe.'));
    };
    script.onerror = () => {
      loading = undefined;
      reject(new Error('Stripe.js could not be loaded.'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

const instances = new Map<string, StripeJs>();

/** One Stripe instance per key, created once the script is in. */
export async function loadStripe(publishableKey: string): Promise<StripeJs> {
  const existing = instances.get(publishableKey);
  if (existing !== undefined) return existing;
  const factory = await loadScript();
  const stripe = factory(publishableKey);
  instances.set(publishableKey, stripe);
  return stripe;
}

/** The Payment Element dressed in the frame's own colours and corners. */
export const STRIPE_APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#5b2ede',
    colorText: '#17112b',
    colorDanger: '#a81f1f',
    fontFamily: 'Manrope, system-ui, sans-serif',
    borderRadius: '14px',
    spacingUnit: '4px',
  },
} as const;

/** Test seam. */
export function resetStripeForTests(): void {
  loading = undefined;
  instances.clear();
}
